import { CommonModule } from "@angular/common";
import { Component, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { BehaviorSubject, firstValueFrom } from "rxjs";
import { User } from "../../services/auth/auth.interface";
import { AuthService } from "../../services/auth/auth.service";

interface OnboardingData {
  grade_levels: string[];
  subject_areas: string[];
  school_type: string;
  teaching_state: string;
}

@Component({
  selector: "app-onboarding",
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: "./onboarding.component.html",
  styleUrl: "./onboarding.component.scss",
})
export class OnboardingPageComponent {
  private authService = inject(AuthService);
  private router = inject(Router);

  isLoading = false;
  error?: string;

  private userSubject = new BehaviorSubject<User | null>(null);

  data: OnboardingData = {
    grade_levels: [],
    subject_areas: [],
    school_type: "",
    teaching_state: "",
  };

  schoolTypeOptions = [
    "Public School",
    "Private School",
    "Charter School",
    "Homeschool",
    "Other",
  ];

  gradeOptions = [
    "Pre-K",
    "Kindergarten",
    "1st Grade",
    "2nd Grade",
    "3rd Grade",
    "4th Grade",
    "5th Grade",
    "6th Grade",
    "7th Grade",
    "8th Grade",
    "9th Grade",
    "10th Grade",
    "11th Grade",
    "12th Grade",
    "Adult",
  ];

  subjectOptions = [
    "English/Language Arts",
    "Math",
    "Science",
    "Social Studies",
    "Art",
    "Music",
    "Physical Education",
    "Health",
    "Technology",
    "Foreign Language",
    "Special Education",
    "Other",
  ];

  stateOptions = [
    "Alabama",
    "Alaska",
    "Arizona",
    "Arkansas",
    "California",
    "Colorado",
    "Connecticut",
    "Delaware",
    "Florida",
    "Georgia",
    "Hawaii",
    "Idaho",
    "Illinois",
    "Indiana",
    "Iowa",
    "Kansas",
    "Kentucky",
    "Louisiana",
    "Maine",
    "Maryland",
    "Massachusetts",
    "Michigan",
    "Minnesota",
    "Mississippi",
    "Missouri",
    "Montana",
    "Nebraska",
    "Nevada",
    "New Hampshire",
    "New Jersey",
    "New Mexico",
    "New York",
    "North Carolina",
    "North Dakota",
    "Ohio",
    "Oklahoma",
    "Oregon",
    "Pennsylvania",
    "Rhode Island",
    "South Carolina",
    "South Dakota",
    "Tennessee",
    "Texas",
    "Utah",
    "Vermont",
    "Virginia",
    "Washington",
    "West Virginia",
    "Wisconsin",
    "Wyoming",
    "Outside US",
  ];

  constructor() {
    this.authService.$user.pipe(takeUntilDestroyed()).subscribe((user) => {
      this.userSubject.next(user);
      if (user) {
        // Pre-populate with any existing data
        this.data.grade_levels = user.grade_levels || [];
        this.data.subject_areas = user.subject_areas || [];
        this.data.school_type = user.school_type || "";
        this.data.teaching_state = user.teaching_state || "";
      }
    });
  }

  toggleGrade(grade: string) {
    const index = this.data.grade_levels.indexOf(grade);
    if (index === -1) {
      this.data.grade_levels.push(grade);
    } else {
      this.data.grade_levels.splice(index, 1);
    }
  }

  isGradeSelected(grade: string): boolean {
    return this.data.grade_levels.includes(grade);
  }

  toggleSubject(subject: string) {
    const index = this.data.subject_areas.indexOf(subject);
    if (index === -1) {
      this.data.subject_areas.push(subject);
    } else {
      this.data.subject_areas.splice(index, 1);
    }
  }

  isSubjectSelected(subject: string): boolean {
    return this.data.subject_areas.includes(subject);
  }

  selectSchoolType(type: string) {
    this.data.school_type = type;
  }

  selectState(state: string) {
    this.data.teaching_state = state;
  }

  async skip() {
    const user = this.userSubject.value;
    if (!user) return;

    await firstValueFrom(
      this.authService.updateUserSettings(user.id, { onboarded: true })
    );
    await this.authService.refreshUser();
    this.router.navigate(["/"]);
  }

  async save() {
    const user = this.userSubject.value;
    if (!user) return;

    this.isLoading = true;
    this.error = undefined;

    try {
      await firstValueFrom(
        this.authService.updateUserSettings(user.id, {
          grade_levels: this.data.grade_levels,
          subject_areas: this.data.subject_areas,
          school_type: this.data.school_type || undefined,
          teaching_state: this.data.teaching_state || undefined,
          onboarded: true,
        })
      );

      await this.authService.refreshUser();

      this.isLoading = false;
      this.router.navigate(["/"]);
    } catch (err: any) {
      this.isLoading = false;
      this.error = err.message;
    }
  }
}
